/*******************************************************************************
 * @ngdoc    object
 * @module   csm.books.InitialSetup.services
 * @name     csm.books.InitialSetup.services.Data.type:SetupJournalItem
 * @constructor
 * @param {object=} data  Object properties to assign.
 * @description
 * Journal Item for use in creating journal entries in Setup for the Fiscal Year
 * start, Tax Year start, and Opening Date.
 *
 * This class tries to normalize the ChangeToBalance and Dr/Cr values when any of
 * the values are set.  Dr/Cr values must be positive and only one can be non-zero.
 * Setting `debit` or `credit` clears the other.  If a negative `debit` is set,
 * the result will be a positive `credit` and a 0 `debit`.  `ChangeToBalance` is
 * set according to the account type.
 *
 * This is a client-side only type. It is not deserialized from the server.
 *******************************************************************************/
/* @ngInject */ export function setupJournalItemConfig(TypeFactoryProvider) {
  TypeFactoryProvider.addType({
    name: 'Data.SetupJournalItem',
    inherits: 'Data.books.JournalItem',
    prototype: function ($injector, SERVER) {
      return Object.defineProperties(
        {
          accountInfo: null, // `AccountInfo` object for `accountID`.
          classInfo: null, // `ClassInfo`   object for `classID`.

          setValue: $injector.invoke(setValue)
        },
        {
          amount: $injector.invoke(amount)
        }
      );
    },
    constructor: function ($injector) {
      return $injector.invoke(constructor);
    }
  });
};

/***************************************************************************
 * Property: amount */
/**
 * ChangeToBalance value. Setter normalizes Dr/Cr values.
 ***************************************************************************/
/* @ngInject */ function amount() {
  return {
    get: function () {
      return this.changeToBalance;
    },
    set: function (v) {
      if (this.accountInfo.isDebitAccount) this.setValue(v, 0);
      else this.setValue(0, v);
    }
  }
}

/***************************************************************************
 * constructor */
/**
 * Builds the line item.
 *
 * @param  acct  Account Information
 ***************************************************************************/
/* @ngInject */ function constructor(Data) {
  return function (acct) {
    this.accountInfo = acct;
    this.accountID = acct.accountID;
    this.classID = acct.classID || 0;
  };
}

/***************************************************************************
 * setValue */
/**
 * Sets Dr/Cr values from the input and normalizes so that only one is not
 * zero and always positive.  Sets ChangeToBalance according to Account Side.
 *
 * @param  dr  Debit Amount
 * @param  cr  Credit Amount
 ***************************************************************************/
/* @ngInject */ function setValue(Data, Tools) {
  return function (dr, cr) {
    Data.books.JournalItem.prototype.setValue.call(this, dr, cr);

    if (this.accountInfo)
      this.changeToBalance = Tools.roundMoney(
        this.accountInfo.isDebitAccount
          ? this.dr - this.cr
          : this.cr - this.dr
      );
  };
}
